/**
 * T-57: patch requests. A coding agent in a task worktree can't change agent instructions, CI, devkit
 * config or the pinned scripts (the actions guard, scripts/dev/lib/guard-actions.mjs). When its task
 * needs such a change, it writes the change as a unified diff and sends it with
 * `sf-agent patch request --reason "…" --file <diff>`: the machine's agent posts it to the hub with the
 * machine token (POST /api/agent/patch-requests), and the agent carries on with the rest of its work.
 *
 * The task's card shows a Patch request button. Its dialog shows the diff beside an explanation a
 * separate AI call wrote from the diff alone (factory/ai-prompts/patch-explain.md) and the agent's own
 * reason. An admin applies it (the machine applies, commits and pushes it in the run's worktree,
 * `git.apply_patch`) or rejects it with a note; either way an @AI builder comment tells the agent.
 */

export const PATCH_REQUEST_STATUSES = ['pending', 'applying', 'applied', 'rejected'] as const;
export type PatchRequestStatus = (typeof PATCH_REQUEST_STATUSES)[number];

/** The longest diff a request may carry (characters). Bigger changes belong in a PR from the Main folder. */
export const PATCH_DIFF_MAX_CHARS = 200_000;
/** The longest reason the agent may give. */
export const PATCH_REASON_MAX_CHARS = 4_000;
/** The longest note a person may give when rejecting. */
export const PATCH_NOTE_MAX_CHARS = 2_000;
/** At most this many files in one request. */
export const PATCH_FILES_MAX = 50;
/** The trailer in the commit a patch lands as, so a repeated apply finds it instead of applying twice. */
export const PATCH_TRAILER = 'Factory-Patch-Request';

/** The AI's explanation of the diff (patch-explain), made after the request arrives. */
export interface PatchExplanation {
  /** What the change does, in a few plain sentences. */
  summary: string;
  /** One line per file or change. */
  changes: string[];
  /** Anything a reviewer should look at twice (a guard weakened, a permission widened); empty when none. */
  risks: string[];
  model: string;
  made_at: string;
}

export interface PatchRequest {
  id: string;
  task_id: string;
  run_id: string | null;
  /** R-<n> of the run that asked, when it's still known. */
  run_number: number | null;
  branch: string;
  /** The files the diff touches, in its order. */
  files: string[];
  diff: string;
  /** The agent's reason, in its own words. */
  reason: string;
  status: PatchRequestStatus;
  explanation: PatchExplanation | null;
  /** Why the explanation couldn't be made, when it couldn't. */
  explanation_error: string | null;
  /** Why the last Apply failed (the request is pending again), or why the push failed after it applied. */
  error: string | null;
  /** The commit it landed as. */
  commit_sha: string | null;
  /** Whether that commit reached GitHub. */
  pushed: boolean | null;
  /**
   * SF-T-176: a pull request from its branch that has already merged: applying it lands on that branch,
   * which no longer reaches the base branch by itself.
   */
  merged_pr_url: string | null;
  /** The reject note. */
  note: string | null;
  decided_by: { user_id: string; name: string } | null;
  decided_at: string | null;
  /**
   * An Apply whose machine never answered, held past the time its git steps could take: Apply again asks
   * the machine, which finds the commit if it landed and never applies twice.
   */
  retryable: boolean;
  /**
   * SF-T-153: while it's applying, when its hold ends (it turns retryable then). Nothing is sent on the
   * live hub when it does, so an open dialog reads again at this time.
   */
  applying_until: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * GET /api/tasks/:taskId/patch-requests/:id/check: whether Apply would work now, asked of the machine
 * (git.check_patch) when the dialog opens. `stale`: the branch moved on, or its files have uncommitted
 * work; `unknown`: the machine couldn't say (offline, too old).
 */
export interface PatchCheck {
  state: 'applies' | 'applied' | 'stale' | 'unknown';
  detail: string | null;
  checked_at: string;
}

/** GET /api/tasks/:taskId/patch-requests */
export interface PatchRequestListResponse {
  patch_requests: PatchRequest[];
}

/** POST /api/agent/patch-requests (Bearer machine token): the request `sf-agent patch request` sends. */
export interface AgentPatchRequestBody {
  project_id: string;
  /** The branch checked out in the worktree it was asked from. */
  branch: string;
  /** That worktree's top level on the machine. */
  worktree_path: string;
  /** HEAD when it was asked. */
  base_commit: string | null;
  diff: string;
  reason: string;
}

export interface AgentPatchRequestResponse {
  id: string;
  /** The task's ref, e.g. SF-T-57. */
  task_ref: string;
  run_number: number;
  files: string[];
  /**
   * SF-T-176: the PR the branch's newest run opened, when it already merged: the request is stored, but
   * applied it lands on a branch nothing brings to the base branch. Older hubs leave it out.
   */
  merged_pr_url?: string | null;
}

/** One file in a unified diff, for the dialog's viewer. */
export interface DiffFile {
  /** The path before (null for a new file). */
  oldPath: string | null;
  /** The path after (null for a deleted file). */
  newPath: string | null;
  /** Header lines (index, mode, rename) before the first hunk. */
  header: string[];
  hunks: DiffHunk[];
  added: number;
  removed: number;
  /** A binary file: git can't show its lines. */
  binary: boolean;
}

export interface DiffHunk {
  /** The `@@ -a,b +c,d @@ …` line. */
  header: string;
  lines: DiffLine[];
}

export interface DiffLine {
  kind: 'add' | 'remove' | 'context' | 'note';
  text: string;
  /** Line numbers before and after (null where the line isn't on that side). */
  oldLine: number | null;
  newLine: number | null;
}

const stripPrefix = (path: string): string | null => {
  const p = path.trim().replace(/^"(.*)"$/, '$1');
  if (p === '/dev/null') return null;
  return p.replace(/^[ab]\//, '');
};

/** Splits a `diff --git a/x b/y` line into its two paths (quoted or not). */
function gitHeaderPaths(line: string): { oldPath: string | null; newPath: string | null } {
  const rest = line.slice('diff --git '.length);
  const quoted = /^"a\/(.*)" "b\/(.*)"$/.exec(rest);
  if (quoted) return { oldPath: quoted[1]!, newPath: quoted[2]! };
  // Unquoted: "a/<p> b/<p>"; with a rename the two differ, so split on " b/" from the middle.
  const half = /^a\/(.*) b\/(.*)$/.exec(rest);
  if (half) return { oldPath: half[1]!, newPath: half[2]! };
  return { oldPath: null, newPath: null };
}

const HUNK_RE = /^@@ -(\d+)(?:,(\d+))? \+(\d+)(?:,(\d+))? @@/;

/**
 * A unified diff (git's, or plain `diff -u`) as files and hunks. Lenient: lines it can't place are
 * kept as notes, so the viewer never hides part of what will be applied.
 */
export function parseUnifiedDiff(diff: string): DiffFile[] {
  const files: DiffFile[] = [];
  let file: DiffFile | null = null;
  let hunk: DiffHunk | null = null;
  let oldNo = 0;
  let newNo = 0;
  // Lines the current hunk still has on each side: at 0 and 0 it's over, so what follows is headers.
  let oldLeft = 0;
  let newLeft = 0;
  const start = (paths: { oldPath: string | null; newPath: string | null }): DiffFile => {
    const f: DiffFile = { ...paths, header: [], hunks: [], added: 0, removed: 0, binary: false };
    files.push(f);
    hunk = null;
    return f;
  };
  const lines = diff.replace(/\r\n/g, '\n').split('\n');
  if (lines.at(-1) === '') lines.pop();
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!;
    if (line.startsWith('diff --git ')) {
      file = start(gitHeaderPaths(line));
      continue;
    }
    // A plain `--- a/x` / `+++ b/x` pair outside a hunk starts a file (or names the git one's paths).
    if (hunk && oldLeft <= 0 && newLeft <= 0 && !line.startsWith('\\')) hunk = null;
    if (line.startsWith('--- ') && lines[i + 1]?.startsWith('+++ ') && !hunk) {
      const oldPath = stripPrefix(line.slice(4).split('\t')[0]!);
      const newPath = stripPrefix(lines[i + 1]!.slice(4).split('\t')[0]!);
      if (!file || file.hunks.length) file = start({ oldPath, newPath });
      else {
        file.oldPath = oldPath;
        file.newPath = newPath;
      }
      i++;
      continue;
    }
    const m = HUNK_RE.exec(line);
    if (m && file) {
      hunk = { header: line, lines: [] };
      file.hunks.push(hunk);
      oldNo = Number(m[1]);
      newNo = Number(m[3]);
      oldLeft = m[2] === undefined ? 1 : Number(m[2]);
      newLeft = m[4] === undefined ? 1 : Number(m[4]);
      continue;
    }
    if (!file) {
      // Text before the first file (a commit message from git format-patch): not part of the change.
      continue;
    }
    if (!hunk) {
      if (/^new file mode /.test(line)) file.oldPath = null;
      if (/^deleted file mode /.test(line)) file.newPath = null;
      if (/^Binary files |^GIT binary patch/.test(line)) file.binary = true;
      file.header.push(line);
      continue;
    }
    const current: DiffHunk = hunk;
    if (line.startsWith('+')) {
      current.lines.push({ kind: 'add', text: line.slice(1), oldLine: null, newLine: newNo++ });
      newLeft--;
      file.added++;
    } else if (line.startsWith('-')) {
      current.lines.push({ kind: 'remove', text: line.slice(1), oldLine: oldNo++, newLine: null });
      oldLeft--;
      file.removed++;
    } else if (line.startsWith(' ') || line === '') {
      current.lines.push({ kind: 'context', text: line.slice(1), oldLine: oldNo++, newLine: newNo++ });
      oldLeft--;
      newLeft--;
    } else {
      // "\ No newline at end of file", or anything else.
      current.lines.push({ kind: 'note', text: line, oldLine: null, newLine: null });
    }
  }
  return files;
}

/** A file's header lines worth showing (mode, rename, symlink): all but git's `index` line and an ordinary file's new or deleted mode. */
export function diffFileNotes(file: Pick<DiffFile, 'header'>): string[] {
  // An ordinary new or deleted file says so with its badge; any other mode (executable, symlink) shows.
  return file.header.filter((l) => l.trim() && !/^index [0-9a-f]+\.\.[0-9a-f]+/.test(l) && !/^(new|deleted) file mode 100644$/.test(l));
}

/** Whether the file is, or becomes, a symlink (mode 120000): its "content" is where it points. */
export function diffFileSymlink(file: Pick<DiffFile, 'header'>): boolean {
  return file.header.some((l) => /\b120000\b/.test(l));
}

/** A file's path to show and to commit: the new path, else the old one (a deleted file). */
export function diffFilePath(file: Pick<DiffFile, 'oldPath' | 'newPath'>): string {
  return file.newPath ?? file.oldPath ?? '';
}

/** Every path the diff touches (both sides of a rename), in order, without repeats. */
export function patchFiles(diff: string): string[] {
  const out: string[] = [];
  for (const f of parseUnifiedDiff(diff)) {
    for (const p of [f.oldPath, f.newPath]) if (p && !out.includes(p)) out.push(p);
  }
  return out;
}

/** Why a diff can't be sent, or null when it can: empty, too big, no files, a path outside the checkout. */
export function patchProblem(diff: string): string | null {
  if (!diff.trim()) return 'the diff is empty';
  if (diff.length > PATCH_DIFF_MAX_CHARS) return `the diff is ${diff.length} characters; the most is ${PATCH_DIFF_MAX_CHARS}`;
  const parsed = parseUnifiedDiff(diff);
  const files = patchFiles(diff);
  if (!files.length) return "it isn't a unified diff (no --- / +++ file headers found)";
  // Bytes nobody can read beside the diff can't be approved.
  const binary = parsed.find((f) => f.binary);
  if (binary) return `${diffFilePath(binary)} is a binary change, which can't be reviewed in the dialog`;
  if (files.length > PATCH_FILES_MAX) return `it touches ${files.length} files; the most is ${PATCH_FILES_MAX}`;
  const outside = files.find((f) => f.startsWith('/') || f.split('/').includes('..') || f.startsWith('.git/'));
  if (outside) return `${outside} isn't a path inside the checkout`;
  return null;
}

/** The first line of the agent's reason, short, for the commit subject. */
export function patchSubject(reason: string, max = 60): string {
  const first = reason.trim().split('\n')[0]!.trim().replace(/\s+/g, ' ');
  return first.length > max ? `${first.slice(0, max - 1).trimEnd()}…` : first || 'a change the guards keep the agent from making';
}

/** The commit message a patch lands as: the reason, who approved it, and the trailer. */
export function patchCommitMessage(p: { id: string; reason: string; approvedBy: string; taskRef: string }): string {
  return [
    `${p.taskRef}: ${patchSubject(p.reason)}`,
    '',
    withoutTrailers(p.reason),
    '',
    `A patch request from the AI builder, approved and applied by ${p.approvedBy} in the factory.`,
    '',
    `${PATCH_TRAILER}: ${p.id}`,
  ].join('\n');
}

/** A trailer line in a reason would pass for this or another request's (git.apply_patch looks them up). */
function withoutTrailers(reason: string): string {
  return reason
    .trim()
    .split('\n')
    .filter((line) => !new RegExp(`^\\s*${PATCH_TRAILER}\\s*:`, 'i').test(line))
    .join('\n');
}

/**
 * SF-T-107: the one commit Apply all lands a task's waiting requests as. Each request's files and reason,
 * then one trailer per request, so a repeated Apply (of all, or of any one) finds the commit by any of them.
 */
export function patchBatchCommitMessage(p: { requests: Array<{ id: string; reason: string; files: string[] }>; approvedBy: string; taskRef: string }): string {
  const files = [...new Set(p.requests.flatMap((r) => r.files))];
  return [
    `${p.taskRef}: ${p.requests.length} patch requests (${filesPhrase(files).replace(/`/g, '')})`,
    '',
    ...p.requests.flatMap((r, i) => [`${i + 1}. ${r.files.join(', ')}`, '', withoutTrailers(r.reason), '']),
    `Patch requests from the AI builder, approved and applied together by ${p.approvedBy} in the factory.`,
    '',
    ...p.requests.map((r) => `${PATCH_TRAILER}: ${r.id}`),
  ].join('\n');
}

/**
 * SF-T-107: several requests' diffs as one, in the order given, for one git apply. git applies a file's
 * patches in turn when it appears more than once, so two requests may touch the same file.
 */
export function joinPatchDiffs(diffs: string[]): string {
  return diffs.map((d) => (d.endsWith('\n') ? d : `${d}\n`)).join('');
}

/** "a.yml", "a.yml and b.md", "a.yml, b.md and 3 more", for messages. */
export function filesPhrase(files: string[], shown = 2): string {
  const code = (f: string) => `\`${f}\``;
  if (files.length <= shown) return files.length === 2 ? `${code(files[0]!)} and ${code(files[1]!)}` : files.map(code).join(', ');
  return `${files.slice(0, shown).map(code).join(', ')} and ${files.length - shown} more`;
}
